import { HttpBackend, HttpClient } from '@angular/common/http';
import { Inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { Configuration } from 'src/app/shared/models/configuration.model';
import { SystemData } from 'src/app/shared/models/system-data.model';
import { DataService } from '../services/data.service';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  public configuration!: Configuration;
  private readonly http: HttpClient;
  private readonly baseRoute: string;
  public canAccessLimitSets!: boolean;
  public allowParametersOnlySearch!: boolean;
  public allowExtendedTestParameterSearch!: boolean;
  public defaultStyleTemplateCode = signal<string | null>(null);
  public isConfigurationsLoaded$ = new BehaviorSubject<boolean>(false);
  public ComplianceLevelMap = signal<Record<string, string[]>>({});

  private readonly errorSubject = new BehaviorSubject<boolean>(false);
  public error$ = this.errorSubject.asObservable();
  public assessmentDisplayMode = '';
  public enableGroups!: boolean;

  constructor(
    @Inject('BASE_URL') public readonly baseUrl: string,
    private readonly dataService: DataService,
    handler: HttpBackend
  ) {
    // skip interceptors as they required logging module but it is not available at that stage yet (as depending on present service)
    this.http = new HttpClient(handler);
    this.baseRoute = `${baseUrl}bff/configuration/`;
  }

  public emitGlobalError(): void {
    this.errorSubject.next(true);

    setTimeout(() => {
      this.clearGlobalError();
    }, 1000);
  }

  public clearGlobalError(): void {
    this.errorSubject.next(false);
  }

  public async setConfiguration(): Promise<Configuration> {
    return new Promise((resolve, reject) => {
      const path = environment.configurationPath;
      this.http.get<Configuration>(path).subscribe({
        next: (response: Configuration) => {
          this.configuration = response;
          resolve(this.configuration);
        },
        error: (err) => {
          reject(new Error(`Failed to fetch configuration: ${err?.message || 'Unknown error'}`));
        }
      });
    });
  }

  public getSystemInitialData(): Observable<SystemData> {
    const api = `${this.baseRoute}system-data`;
    return this.dataService.get<SystemData>(api, true).pipe(
      map((res: SystemData) => {
        this.canAccessLimitSets = res.canAccessLimitSets;
        this.allowParametersOnlySearch = res.allowParametersOnlySearch;
        this.allowExtendedTestParameterSearch = res.allowExtendedTestParameterSearch;
        this.defaultStyleTemplateCode.set(res.defaultStyleTemplateCode);
        this.ComplianceLevelMap.set(res.complianceLevelMap);
        this.isConfigurationsLoaded$.next(true);
        this.assessmentDisplayMode = res.assessmentDisplayMode;
        this.enableGroups = res.enableGroup;
        return res;
      }),
      catchError((err: any) => {
        return of(err?.error);
      })
    );
  }
}
